// Copyright (c) StrangeBee 2023

import { DashboardController, DashboardInput, DashboardOutput } from 'thehive-sdk';
import { periods } from './DashboardPanel/DashboardPeriod/DashboardPeriod';
import moment from 'moment';

export interface DashboardEventList {
    onCreate: (dashboard: DashboardInput) => Promise<DashboardOutput | void>;
    onEdit: (dashboard: Partial<DashboardInput>, dashboardId: string, showNotification?: boolean) => Promise<void>;
    onChangeOwner: (owner: string, dashboardId: string, showNotification?: boolean) => Promise<void>;
    onDelete: (dashboardId: string) => Promise<void>;
}

interface DashboardEventListParams {
    locale: (label: string) => string;
    controller: DashboardController;
    onSuccess?: (count?: boolean, message?: string) => void;
    onError?: (label?: string) => void;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
}: DashboardEventListParams): DashboardEventList => ({
    onCreate: (dashboard) =>
        controller
            .create<DashboardInput, DashboardOutput>({
                ...dashboard,
                definition: dashboard.definition || {
                    period: 'last3Months',
                    customPeriod: {
                        fromDate: periods['last3Months']
                            .from?.(moment())
                            .hours(0)
                            .minutes(0)
                            .seconds(0)
                            .milliseconds(0)
                            .valueOf(),
                        toDate: moment().hours(23).minutes(59).seconds(59).milliseconds(999).valueOf(),
                    },
                    items: [{ type: 'container', items: [] }],
                    cache: true,
                },
            })
            .then((dashboard) => {
                onSuccess?.(true, locale('notification.dashboard.create'));
                return dashboard;
            })
            .catch(onError),
    onEdit: (dashboard, dashboardId, showNotification = true) =>
        controller
            .edit<Partial<DashboardInput>, void>(dashboard, dashboardId)
            .then(() => onSuccess?.(false, showNotification ? locale('notification.dashboard.edit') : undefined))
            .catch(onError),
    onChangeOwner: (owner, dashboardId, showNotification = true) =>
        controller
            .changeOwner(owner, dashboardId)
            .then(() =>
                onSuccess?.(false, showNotification ? locale('notification.dashboard.owner-change') : undefined),
            )
            .catch(onError),
    onDelete: (dashboardId) =>
        controller
            .delete(dashboardId)
            .then(() => onSuccess?.(true, locale('notification.dashboard.delete')))
            .catch(onError),
});
